import React, {
    useImperativeHandle,
    forwardRef,
    useCallback,
    useReducer
} from 'react'
import moment from 'moment'
import {Modal, Card, Colors, Row, H4, S1, S2, P, Spinner, IconButton, Button, Link, Alert, ScrollView, Stack} from 'src/components/UI'
import dbCodes from '../../../server/dbCodes.js'
import {Order, Collo, Reseller, Customer, HistoryEntry, Location, OrderAddress} from 'types/types'
import calcDistance from '@utils/calcDistance.js'
import {isRetourOrder} from '@utils/retourAddresses.js'
import {base64ImageToDataUrl} from '@utils/imageDataUrl.js'
import {emit} from 'src/socketio/socketEmit.js'
import ViewOrderAddress from './ViewOrderAddress.js'
import {orderTrackTraceLink} from '../../../utils/trackTraceLink.js'

export interface ViewOrderModalCustomerRef {
    open: (id: string | Order) => Promise<void>
    close: () => void
}

interface ViewOrderModalCustomerProps {
    history: any
    reseller: Reseller
    user: Customer
    onClickEdit: (order: Order) => void
    onClickCopy: (id: string) => void
    onClickCancel: (id: string) => Promise<void>
    onClickRemove: (id: string) => void
    onClickPrintLabels: (orderId: string, addressIndex?: number, colloIndex?: number) => void
    addressBook: any[]
}

type ViewOrderModalCustomerState = {
    modalIsOpen: boolean
    orderLoading: boolean
    orderError: string | null
    order: Order | null
    removeAll: boolean
    entryPopup: any
    entryPopupView: string
    loading: boolean
    historyComment: string
    message: string
    error: string
}

const initialState: ViewOrderModalCustomerState = {
    modalIsOpen: false,
    orderLoading: false,
    orderError: null,
    order: null,
    removeAll: false,
    entryPopup: null,
    entryPopupView: '',
    loading: false,
    historyComment: '',
    message: '',
    error: ''
}

const ViewOrderModalCustomer = forwardRef<ViewOrderModalCustomerRef, ViewOrderModalCustomerProps>((props, ref) => {
    const [state, setState] = useReducer((state: ViewOrderModalCustomerState, newState: Partial<ViewOrderModalCustomerState>) => ({...state, ...newState}), structuredClone(initialState))

    useImperativeHandle(ref, () => ({
        open: async (id: string) => {
            setState({
                modalIsOpen: true,
                orderLoading: true
            })
            try {
                const {data: order} = await emit<Order>('orders.getOne', id)

                console.log(order)

                setState({
                    orderLoading: false,
                    orderError: null,
                    order
                })
            } catch (err: any) {
                setState({
                    orderLoading: false,
                    orderError: err?.message || String(err)
                })
            }
        },
        close
    }))

    const close = () => {
        setState({...initialState})
        // If you need to push history, do it here
        // props.history.push('/klant/orders')
    }

    // Event handlers
    const onClickGotoTrackTrace = useCallback((order: Order) => {
        if (order.noTrackTrace) {
            return
        }

        window.open(orderTrackTraceLink(order))
    }, [])

    const onClickViewImage = useCallback((data: string, event?: React.MouseEvent) => {
        event?.preventDefault()
        const w = window.open('about:blank')
        const image = new window.Image()
        image.src = data
        setTimeout(() => {
            w?.document.write(image.outerHTML)
        }, 0)
    }, [])

    const onClickLocation = useCallback((location: Location, event?: React.MouseEvent) => {
        event?.preventDefault()
        window.open(`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`)
    }, [])

    const onClickOpenImage = useCallback(async (entry: HistoryEntry) => {
        if (entry.imageId) {
            const {data: file} = await emit<string>('orders.getFile', entry.imageId)
            entry.image = base64ImageToDataUrl(file)

            setState({entryPopup: entry, entryPopupView: 'image'})
        } else if (entry.imageIds?.length > 0) {
            const images: string[] = []

            for (const imageId of entry.imageIds) {
                try {
                    const {data: file} = await emit<string>('orders.getFile', imageId)
                    images.push(base64ImageToDataUrl(file))
                } catch (err) {
                    console.error(err)
                }
            }

            setState({entryPopup: {...entry, images}, entryPopupView: 'images'})
        } else {
            setState({entryPopup: entry, entryPopupView: 'image'})
        }
    }, [])

    const onClickOpenSignature = useCallback(async (entry: HistoryEntry) => {
        if (entry.signatureId) {
            const {data: file} = await emit<string>('orders.getFile', entry.signatureId)
            entry.signature = `data:image/png;base64,${file}`

            setState({entryPopup: entry, entryPopupView: 'signature'})
        }
    }, [])

    const closeEntryPopup = () => setState({entryPopup: '', entryPopupView: ''})

    const {modalIsOpen, orderLoading, orderError, order, entryPopup, entryPopupView, message, error} = state
    const {history, reseller, user, onClickEdit, onClickCopy, onClickPrintLabels, onClickCancel, onClickRemove} = props

    const {statusColor, status, subStatus} = Colors.orderStatusColor(order)

    const statusHistory = order?.history?.filter((entry) => typeof entry.status === 'number')

    return (
        <Modal show={modalIsOpen} onClose={() => setState({...initialState})}>
            <div style={{position: 'relative'}}>
                <Row style={{alignItems: 'center', marginBottom: 12}}>
                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                        <H4>Rit info</H4>
                        <div style={{flex: 1}} />
                    </Row>

                    {!orderLoading && order && (
                        <>
                            {user.permissions?.orders &&
                                <IconButton
                                    tooltip='Bewerk rit'
                                    disabled={statusHistory?.length !== 1}
                                    onClick={() => {
                                        close()
                                        onClickEdit(order)
                                    }}
                                >
                                    <i className='mdi mdi-pencil' />
                                </IconButton>
                            }

                            {user.permissions?.orders &&
                                <IconButton
                                    tooltip='Kopieer rit'
                                    onClick={() => {
                                        close()
                                        onClickCopy(order._id)
                                    }}
                                >
                                    <i className='mdi mdi-content-copy' />
                                </IconButton>
                            }

                            <IconButton
                                tooltip='Print labels'
                                onClick={() => onClickPrintLabels(order._id)}
                            >
                                <i className='mdi mdi-printer' />
                            </IconButton>

                            { user.permissions?.orders &&
                                <IconButton
                                    tooltip='Annuleer rit'
                                    icon='mdi mdi-close-circle'
                                    onClick={async () => {
                                        await onClickCancel(order._id)
                                        const {data: updatedOrder} = await emit<Order>('orders.getOne', order._id)

                                        console.log(updatedOrder)
                                        setState({order: updatedOrder})
                                    }}
                                    disabled={![dbCodes.status.verwacht(), dbCodes.status.gereed(), dbCodes.status.geannuleerd()].includes(order.status)}
                                />
                            }

                            {user.permissions?.orders &&
                                <IconButton
                                    tooltip='Verwijder rit'
                                    disabled={statusHistory?.length !== 1 || ['parcelPickup', 'parcelDelivery', 'retour'].includes(order.type)}
                                    onClick={() => onClickRemove(order._id)}
                                >
                                    <i className='mdi mdi-delete' />
                                </IconButton>
                            }

                        </>
                    )}

                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                        <div style={{flex: 1}} />
                        <IconButton onClick={() => setState({...initialState})}>
                            <i style={{color: Colors.buttonSolid}} className='mdi mdi-close' />
                        </IconButton>
                    </Row>
                </Row>

                {orderLoading || !order ?

                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250}}>
                        <Spinner color={Colors.textDark} />
                    </div> :

                    <>
                        <Card style={{marginBottom: 24}}>
                            <div style={{marginBottom: 12, width: '100%'}}>
                                <div style={{display: 'flex'}}>

                                    <div style={{display: 'flex', flex: 1}}>
                                        <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-calendar' />
                                        <P>{moment(order.date).format('DD-MM-YYYY')}</P>
                                    </div>

                                    <div style={{display: 'flex', flex: 1}}>
                                        <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-web' />

                                        {order.noTrackTrace ?
                                            <S1>{order.trackTrace}</S1> :
                                            <Link onClick={() => onClickGotoTrackTrace(order)}>{order.trackTrace}</Link>
                                        }
                                    </div>
                                </div>

                                {order.notes &&
                                    <div style={{display: 'flex'}}>
                                        <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-message-outline' />
                                        <P>{order.notes}</P>
                                    </div>
                                }
                            </div>

                            <Stack style={{flex: 1, justifyContent: 'center', alignItems: 'center', background: statusColor, height: 42}}>
                                {subStatus && subStatus !== dbCodes.status[dbCodes.status.geannuleerd()] ?
                                    <S1 ellipsis>{subStatus}</S1> :
                                    <S1 ellipsis>{status}</S1>
                                }
                            </Stack>
                        </Card>

                        <S1 style={{marginBottom: 12}}>Adressen</S1>

                        <div style={{width: '100%', marginBottom: 24}}>
                            {isRetourOrder(order) && order.originalAddresses?.map((address: OrderAddress, addressIndex: number) => {
                                return (
                                    <ViewOrderAddress
                                        key={`originalAddress${addressIndex}`}
                                        order={order}
                                        address={address}
                                        reseller={reseller}
                                        disabled
                                    />
                                )
                            })}

                            {order.addresses.map((address: OrderAddress, addressIndex: number) => {
                                return (
                                    <ViewOrderAddress
                                        key={`address${addressIndex}`}
                                        order={order}
                                        address={address}
                                        reseller={reseller}
                                        isRetour={isRetourOrder(order)}
                                    />
                                )
                            })}
                        </div>

                        <S1 style={{marginBottom: 12}}>Historie</S1>

                        <ScrollView style={{maxHeight: 250}}>
                            {[...order.history].reverse().map((entry: HistoryEntry, index: number) => {
                                let deviationMeters: number | undefined

                                if (entry.location && order.addresses[entry.addressIndex]?.position) {
                                    deviationMeters = Math.floor(calcDistance(entry.location, order.addresses[entry.addressIndex].position)) - Math.floor(entry.location.accuracy)
                                }

                                if (entry.type === 'invoice') {
                                    return null
                                }

                                return (
                                    <Row key={`history${index}`} style={{marginBottom: 3, flexShrink: 0}}>
                                        <i className='mdi mdi-circle-small' style={{marginRight: 6, fontSize: 16, color: Colors.grey60}} />
                                        <Row style={{paddingLeft: 6, paddingRight: 6, flex: 1, flexDirection: 'column', minWidth: 'auto'}}>
                                            <Row style={{alignItems: 'center'}}>
                                                <P>{entry.description || (typeof entry.status === 'number' ? dbCodes.status[entry.status] : '')}</P>
                                            </Row>

                                            {entry.colli?.length > 0 && (
                                                <>
                                                    {entry.colli.map((collo: Collo, idx: number) => {
                                                        const {status, reason, statusColor} = Colors.colloStatusColor(entry.type, collo)
                                                        const isScanned = entry?.scanned?.findIndex((barcode: string) => barcode === collo.barcode) > -1

                                                        return (
                                                            <Row key={`collo${idx}`}>
                                                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                                                                    <i className='mdi mdi-circle' style={{marginRight: 12, marginTop: 4, fontSize: 10, color: statusColor}} />
                                                                    {isScanned && <i className='mdi mdi-barcode-scan ' style={{marginRight: 6}} />}
                                                                    <P>{`${reason || status}${collo.reasonSpecification ? ` - ${collo.reasonSpecification}` : ''}: Collo ${collo.index + 1}${collo.barcode ? ` - ${collo.barcode}` : ''}`}</P>
                                                                </Row>
                                                            </Row>
                                                        )
                                                    })}
                                                </>
                                            )}
                                            {entry.comment && <S2 style={{whiteSpace: 'pre'}}>{entry.comment}</S2>}
                                        </Row>

                                        <Row style={{paddingLeft: 6, paddingRight: 6, width: 180, marginTop: -10, minWidth: 'auto'}}>
                                            {order.addresses[entry.addressIndex]?.position && (
                                                <IconButton onClick={() => setState({entryPopup: entry, entryPopupView: 'location'})} style={{color: !entry.location ? Colors.grey60 : (reseller.settings.deliveryRadiusWarning && deviationMeters >= (reseller.settings.maxDeliveryRadius || 40)) ? Colors.errorBright : Colors.buttonSolid}}>
                                                    <i className='mdi mdi-map-marker-radius' />
                                                </IconButton>
                                            )}
                                            {(entry.signee || entry.signature || entry.signatureId) && (
                                                <IconButton onClick={() => onClickOpenSignature(entry)}>
                                                    {(entry.signature || entry.signatureId) ? <i className='mdi mdi-fountain-pen-tip' /> : <i className='mdi mdi-signature-text' />}
                                                </IconButton>
                                            )}
                                            {entry.documentId && (
                                                <IconButton onClick={() => setState({entryPopup: entry, entryPopupView: 'documentId'})}>
                                                    <i className='mdi mdi-card-account-details-outline' />
                                                </IconButton>
                                            )}

                                            {entry.verificationCode && (
                                                <IconButton onClick={() => setState({entryPopup: entry, entryPopupView: 'verificationCode'})}>
                                                    <i className='mdi mdi-shield-key-outline' />
                                                </IconButton>
                                            )}

                                            {(entry.image || entry.imageId || entry.imageIds?.length > 0) && (
                                                <IconButton onClick={() => onClickOpenImage(entry)}>
                                                    <i className='mdi mdi-image-outline' />
                                                </IconButton>
                                            )}
                                        </Row>
                                        <Row style={{paddingLeft: 6, paddingRight: 6, width: 150, minWidth: 'auto'}}>
                                            <S2>{`${entry.timestamp ? moment(entry.timestamp).format('DD-MM-YYYY HH:mm:ss') : entry.time}`}</S2>
                                        </Row>
                                    </Row>
                                )
                            })}
                        </ScrollView>
                    </>
                }
                {message && <Alert style={{marginBottom: 6}} variant='success'>{message}</Alert>}

                {error && <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>}

                {orderError && <Alert style={{marginBottom: 6}} variant='danger'>{orderError}</Alert>}

                {entryPopup && (
                    <Card style={{position: 'absolute', bottom: -12, left: 800, width: 400, overflowY: 'auto', padding: '12px 12px 24px 12px', background: Colors.backgroundNeutral}}>
                        <Row style={{alignItems: 'center'}}>
                            <H4>
                                {entryPopupView === 'location' && 'Locatie'}
                                {entryPopupView === 'signature' && 'Handtekening'}
                                {entryPopupView === 'documentId' && 'Document ID (laatste 4 tekens)'}
                                {entryPopupView === 'verificationCode' && 'Verificatiecode'}
                                {entryPopupView === 'image' && 'Foto'}
                                {entryPopupView === 'images' && 'Foto'}
                            </H4>

                            <div style={{flex: 1}} />

                            <IconButton onClick={closeEntryPopup}>
                                <i style={{color: Colors.buttonSolid}} className='mdi mdi-close' />
                            </IconButton>
                        </Row>

                        <Row style={{marginTop: 24, marginBottom: 24}}>
                            <i className='mdi mdi-circle' style={{marginRight: 6, marginTop: 3, fontSize: 12, color: Colors.addressColliStatus(order, order.addresses[entryPopup.addressIndex], entryPopup.colli).statusColor || Colors.grey40}} />

                            <Row style={{paddingLeft: 6, paddingRight: 6, flex: 1, flexDirection: 'column', minWidth: 'auto'}}>
                                <Row style={{alignItems: 'center'}}>
                                    {entryPopup.type === 'invoice' ?
                                        (
                                            <Link onClick={() => history.push('/facturatie', {invoiceId: entryPopup.invoiceId})}>{entryPopup.description}</Link>
                                        ) :
                                        (
                                            <P>{entryPopup.description || (typeof entryPopup.status === 'number' ? dbCodes.status[entryPopup.status] : '')}</P>
                                        )}
                                </Row>
                            </Row>
                            <Row style={{paddingLeft: 6, paddingRight: 6, width: 200, minWidth: 'auto'}}>
                                <S2>{`${entryPopup.who} | ${entryPopup.timestamp ? moment(entryPopup.timestamp).format('DD-MM-YYYY HH:mm:ss') : entryPopup.time}`}</S2>
                            </Row>
                        </Row>
                        {entryPopupView === 'location' && (
                            <>
                                {entryPopup.location && order?.addresses[entryPopup.addressIndex].position ?
                                    (
                                        <>
                                            <S1>Afwijking met adres</S1>
                                            <P>{Math.floor(calcDistance(entryPopup.location, order?.addresses[entryPopup.addressIndex].position))} m ±{parseInt(entryPopup.location.accuracy)} m</P>
                                            <br />
                                            <Button variant='text' onClick={() => onClickLocation(entryPopup.location)}>
                                                Bekijk op kaart
                                            </Button>
                                        </>
                                    ) :
                                    (
                                        <P>Locatie is niet beschikbaar</P>
                                    )}
                            </>
                        )}
                        {entryPopupView === 'signature' && (
                            <>
                                {entryPopup?.signee && (
                                    <>
                                        <S1>Getekend door</S1>
                                        <P>{entryPopup?.signee}</P>
                                    </>
                                )}
                                {entryPopup?.signature && (
                                    <>
                                        <img style={{height: 200, width: 200, marginRight: 12, objectFit: 'contain'}} src={entryPopup?.signature} />
                                        <IconButton onClick={() => onClickViewImage(entryPopup?.signature)}>
                                            <i className='mdi mdi-magnify-plus-outline' />
                                        </IconButton>
                                    </>
                                )}
                            </>
                        )}
                        {entryPopupView === 'documentId' && (
                            <Row style={{alignItems: 'center'}}>
                                <i style={{marginRight: 6}} className='mdi mdi-card-account-details-outline' />
                                <P>{entryPopup.documentId}</P>
                            </Row>
                        )}
                        {entryPopupView === 'verificationCode' && (
                            <Row style={{alignItems: 'center'}}>
                                <i style={{marginRight: 6}} className='mdi mdi-shield-key-outline' />
                                <P>{entryPopup.verificationCode}</P>
                            </Row>
                        )}
                        {entryPopupView === 'image' && (
                            <>
                                <img style={{height: 200, width: '100%', objectFit: 'contain'}} src={entryPopup.image} />
                                <IconButton onClick={() => onClickViewImage(entryPopup.image)}>
                                    <i className='mdi mdi-magnify-plus-outline' />
                                </IconButton>
                            </>
                        )}

                        {entryPopupView === 'images' && (
                            <>
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
                                    {entryPopup.images.map((image: string, index: number) => {
                                        return (
                                            <div key={index}>
                                                <img
                                                    style={{height: 100, width: 120, objectFit: 'contain'}}
                                                    src={image}
                                                />
                                                <IconButton onClick={() => onClickViewImage(image)}>
                                                    <i className='mdi mdi-magnify-plus-outline' />
                                                </IconButton>
                                            </div>
                                        )
                                    })}
                                </div>
                            </>
                        )}
                    </Card>
                )}
            </div>
        </Modal>
    )
})

ViewOrderModalCustomer.displayName = 'ViewOrderModalCustomer'

export default ViewOrderModalCustomer
